/**
 * SlushLending Strategy
 */
import { Decimal } from 'decimal.js';
import { BaseStrategy } from './strategy.js';
import { ALPHAFI_ORACLE, ALPHALEND_LENDING_PROTOCOL_ID, CLOCK_PACKAGE_ID, GLOBAL_CONFIGS, IMAGE_URLS, SUI_SYSTEM_STATE, } from '../utils/constants.js';
import { planSlushWithdraw, totalSlushXTokens } from '../utils/slushPositions.js';
/**
 * SlushLending Strategy for slush lending pools without alpha mining
 */
export class SlushLendingStrategy extends BaseStrategy {
    poolLabel;
    poolObject;
    receiptObjects = [];
    context;
    constructor(poolLabel, poolObject, context) {
        super();
        this.poolLabel = poolLabel;
        this.poolObject = this.parsePoolObject(poolObject);
        this.context = context;
    }
    getPoolLabel() {
        return this.poolLabel;
    }
    getOtherAmount(_amount, _isAmountA) {
        throw new Error('getOtherAmount is not supported for single-asset SlushLending strategy');
    }
    updateReceipts(receipts) {
        this.receiptObjects = this.parseReceiptObjects(receipts);
    }
    /**
     * Returns alpha mining data - SlushLending pools do not support alpha mining
     */
    getAlphaMiningData() {
        return {
            poolId: this.poolLabel.poolId,
            accRewardsPerXtoken: [],
            xTokenSupply: '0',
            receipt: null,
        };
    }
    /**
     * Get the exchange rate for xtoken to underlying token ratio
     * Calculated as tokens_invested / xtoken_supply
     */
    exchangeRate() {
        const tokensInvested = new Decimal(this.poolObject.tokensInvested);
        const xtokenSupply = new Decimal(this.poolObject.xTokenSupply);
        if (xtokenSupply.isZero()) {
            return new Decimal(1);
        }
        return tokensInvested.div(xtokenSupply);
    }
    /**
     * Get comprehensive pool data including TVL and APR information
     */
    async getData() {
        const [alphafi, parent, apr] = await Promise.all([
            this.getTvl(),
            this.getParentTvl(),
            this.context.getAprData(this.poolLabel.poolId),
        ]);
        return {
            poolId: this.poolLabel.poolId,
            strategyType: this.poolLabel.strategyType,
            coinType: this.poolLabel.asset.type,
            poolName: this.poolLabel.poolName,
            apr,
            tvl: {
                alphafi,
                parent,
            },
        };
    }
    /**
     * Calculate total value locked using current asset price
     */
    async getTvl() {
        const coinType = this.poolLabel.asset.type;
        const [price, decimals] = await Promise.all([
            this.context.getCoinPrice(coinType),
            this.context.getCoinDecimals(coinType),
        ]);
        const tokenAmount = new Decimal(this.poolObject.tokensInvested).div(new Decimal(10).pow(decimals));
        const usdValue = tokenAmount.mul(price);
        return { tokenAmount, usdValue };
    }
    /**
     * Calculate parent protocol TVL (Alphalend only)
     */
    async getParentTvl() {
        const protocol = this.poolLabel.parentProtocol;
        if (protocol !== 'Alphalend') {
            throw new Error(`Unsupported parent protocol for SlushLending: ${protocol}`);
        }
        const [tokenAmount, price] = await Promise.all([
            this.context.getAlphaLendTvl(this.poolLabel.asset.type),
            this.context.getCoinPrice(this.poolLabel.asset.type),
        ]);
        return { tokenAmount, usdValue: tokenAmount.mul(price) };
    }
    /**
     * Calculate user's current pool balance from xToken balance
     * Converts xTokens to underlying tokens via exchange rate
     */
    async getBalance(_userAddress) {
        const totalXTokens = totalSlushXTokens(this.receiptObjects);
        if (totalXTokens === 0n) {
            return { tokenAmount: new Decimal(0), usdValue: new Decimal(0) };
        }
        const xTokens = new Decimal(totalXTokens.toString());
        const [price, exchangeRate, decimals] = await Promise.all([
            this.context.getCoinPrice(this.poolLabel.asset.type),
            Promise.resolve(this.exchangeRate()),
            this.context.getCoinDecimals(this.poolLabel.asset.type),
        ]);
        const tokens = xTokens.mul(exchangeRate).div(new Decimal(10).pow(decimals));
        return { tokenAmount: tokens, usdValue: tokens.mul(price) };
    }
    /**
     * Parse pool object from blockchain response
     */
    parsePoolObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                id: this.getStringField(fields, 'id'),
                xTokenSupply: this.getNestedField(fields, 'xTokenSupply.value'),
                tokensInvested: this.getStringField(fields, 'tokensInvested'),
                interestBasedRewardsData: {
                    rewardBalance: this.getStringField(fields.interest_based_rewards_data, 'reward_balance'),
                    lastUpdatedTimestamp: this.getStringField(fields.interest_based_rewards_data, 'last_updated_timestamp'),
                    rewardShareFromInterestBps: this.getNestedField(fields.interest_based_rewards_data, 'reward_share_from_interest_bps.value'),
                },
                timeBasedRewardsData: {
                    rewardBalance: this.getStringField(fields.time_based_rewards_data, 'reward_balance'),
                    startTime: this.getStringField(fields.time_based_rewards_data, 'start_time'),
                    endTime: this.getStringField(fields.time_based_rewards_data, 'end_time'),
                    rewardPerMs: this.getNestedField(fields.time_based_rewards_data, 'reward_per_ms.value'),
                    lastUpdatedTimestamp: this.getStringField(fields.time_based_rewards_data, 'last_updated_timestamp'),
                },
                positionCount: this.getNestedField(fields, 'positions.size'),
                positionsTableId: this.getNestedField(fields, 'positions.id'),
                feeCollected: this.getStringField(fields, 'fee_collected'),
                depositFee: this.getStringField(fields, 'deposit_fee'),
                depositFeeMaxCap: this.getStringField(fields, 'deposit_fee_max_cap'),
                withdrawalFee: this.getStringField(fields, 'withdrawal_fee'),
                withdrawFeeMaxCap: this.getStringField(fields, 'withdraw_fee_max_cap'),
                investor: {
                    id: this.getStringField(fields.investor, 'id'),
                    marketId: this.getStringField(fields.investor, 'market_id'),
                    positionCap: {
                        clientAddress: this.getNestedField(fields, 'investor.alphalend_position_cap.client_address'),
                        id: this.getNestedField(fields, 'investor.alphalend_position_cap.id'),
                        imageUrl: this.getNestedField(fields, 'investor.alphalend_position_cap.image_url'),
                        positionId: this.getNestedField(fields, 'investor.alphalend_position_cap.position_id'),
                    },
                },
                paused: this.getBooleanField(fields, 'paused', false),
            };
        }, 'Failed to parse Slush Lending pool object');
    }
    /**
     * Parse investor object (not applicable for SlushLending)
     */
    parseInvestorObject(_) {
        throw new Error('Investor object not used for SlushLending');
    }
    /**
     * Parse parent pool object (not applicable for SlushLending)
     */
    parseParentPoolObject(_) {
        throw new Error('Parent pool object not used for SlushLending');
    }
    /**
     * Parse receipt objects from blockchain responses
     */
    parseReceiptObjects(responses) {
        return responses.map((response, index) => {
            return this.safeParseObject(() => {
                const fields = this.extractFields(response);
                return {
                    id: this.getStringField(fields, 'id'),
                    positionCapId: this.getStringField(fields, 'position_cap_id'),
                    poolId: this.getStringField(fields, 'pool_id'),
                    coinType: this.getNestedField(fields, 'coin_type.name'),
                    principal: this.getStringField(fields, 'principal'),
                    xTokens: this.getStringField(fields, 'xtokens') ||
                        this.getStringField(fields, 'x_token_balance') ||
                        this.getStringField(fields, 'xTokenBalance'),
                };
            }, `Failed to parse Slush Lending receipt object at index ${index}`);
        });
    }
    createPositionCap(tx) {
        const urlBytes = Array.from(new TextEncoder().encode(IMAGE_URLS.SLUSH_POSITION_CAP));
        const positionCap = tx.moveCall({
            target: `${this.poolLabel.packageId}::alphalend_slush_pool::create_position_cap`,
            arguments: [tx.pure.vector('u8', urlBytes)],
        });
        return positionCap;
    }
    coinAmountToXToken(amount) {
        const exchangeRate = this.exchangeRate();
        return new Decimal(amount).div(exchangeRate).floor().toString();
    }
    async collectAndSwapRewards(tx) {
        const positionId = this.poolObject.investor.positionCap.positionId;
        const alphalendClient = this.context.alphalendClient;
        const portfolio = await alphalendClient.getUserPortfolioFromPosition(positionId);
        const rewards = portfolio?.rewardsToClaim;
        if (!rewards || rewards.length === 0) {
            console.log('no rewards for pool id: ', this.poolLabel.poolId);
            return;
        }
        const [stsuiCoin, suiCoin, deepCoin, walCoin, usdcCoin] = await this.context.getCoinsBySymbols([
            'stSUI',
            'SUI',
            'DEEP',
            'WAL',
            'USDC',
        ]);
        await alphalendClient.updatePrices(tx, [
            stsuiCoin.coinType,
            suiCoin.coinType,
            deepCoin.coinType,
            walCoin.coinType,
        ]);
        let anyRewardToSui = false;
        for (const reward of rewards) {
            if (reward.coinType !== this.poolLabel.asset.type && reward.coinType !== suiCoin.coinType) {
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphalend_slush_pool::collect_reward_and_swap_bluefin_v2`,
                    typeArguments: [this.poolLabel.asset.type, reward.coinType, suiCoin.coinType],
                    arguments: [
                        tx.object(this.poolLabel.versionId),
                        tx.object(this.poolLabel.poolId),
                        tx.object(ALPHAFI_ORACLE),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdByTypesAndProtocol(reward.coinType, suiCoin.coinType, 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(true),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
                anyRewardToSui = true;
            }
        }
        if (anyRewardToSui && this.poolLabel.asset.type !== suiCoin.coinType) {
            if (this.poolLabel.asset.type === usdcCoin.coinType) {
                // SUI/USDC pool (A=SUI, B=USDC): a2b=true swaps SUI→USDC
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphalend_slush_pool::collect_reward_and_swap_bluefin_v2`,
                    typeArguments: [this.poolLabel.asset.type, suiCoin.coinType, usdcCoin.coinType],
                    arguments: [
                        tx.object(this.poolLabel.versionId),
                        tx.object(this.poolLabel.poolId),
                        tx.object(ALPHAFI_ORACLE),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdBySymbolsAndProtocol('SUI', 'USDC', 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(true),
                        tx.pure.bool(true),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
            }
            else {
                // base/SUI pool (A=base, B=SUI): a2b=false swaps SUI→base
                tx.moveCall({
                    target: `${this.poolLabel.packageId}::alphalend_slush_pool::collect_reward_and_swap_bluefin_v2`,
                    typeArguments: [this.poolLabel.asset.type, this.poolLabel.asset.type, suiCoin.coinType],
                    arguments: [
                        tx.object(this.poolLabel.versionId),
                        tx.object(this.poolLabel.poolId),
                        tx.object(ALPHAFI_ORACLE),
                        tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                        tx.object(await this.context.getPoolIdBySymbolsAndProtocol(this.poolLabel.asset.name, 'SUI', 'bluefin')),
                        tx.object(GLOBAL_CONFIGS.BLUEFIN),
                        tx.pure.bool(false),
                        tx.pure.bool(true),
                        tx.object(CLOCK_PACKAGE_ID),
                    ],
                });
            }
        }
    }
    async deposit(tx, options) {
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        // Get coin object
        const depositCoin = this.context.blockchain.getCoinObject(tx, this.poolLabel.asset.type, options.address, BigInt(options.amount));
        await this.collectAndSwapRewards(tx);
        const positionCap = await this.context.getSlushPositionCapForDeposit(options.address, this.poolLabel.poolId);
        if (!positionCap) {
            const positionCap = this.createPositionCap(tx);
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphalend_slush_pool::user_deposit`,
                typeArguments: [this.poolLabel.asset.type],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    positionCap,
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
            tx.transferObjects([positionCap], options.address);
        }
        else {
            tx.moveCall({
                target: `${this.poolLabel.packageId}::alphalend_slush_pool::user_deposit`,
                typeArguments: [this.poolLabel.asset.type],
                arguments: [
                    tx.object(this.poolLabel.versionId),
                    tx.object(positionCap.id),
                    tx.object(this.poolLabel.poolId),
                    depositCoin,
                    tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                    tx.object(SUI_SYSTEM_STATE),
                    tx.object(CLOCK_PACKAGE_ID),
                ],
            });
        }
    }
    async withdraw(tx, options) {
        if (this.receiptObjects.length === 0) {
            throw new Error('No receipt found for withdraw');
        }
        const alphalendClient = this.context.alphalendClient;
        await alphalendClient.updatePrices(tx, [this.poolLabel.asset.type]);
        // One leg per confirmed position (largest first), each with that position's own cap.
        const legs = planSlushWithdraw(this.receiptObjects, options.withdrawMax ? 'max' : this.coinAmountToXToken(options.amount));
        if (legs.length === 0) {
            throw new Error('Nothing to withdraw');
        }
        await this.collectAndSwapRewards(tx);
        const coins = legs.map((leg) => tx.moveCall({
            target: `${this.poolLabel.packageId}::alphalend_slush_pool::user_withdraw`,
            typeArguments: [this.poolLabel.asset.type],
            arguments: [
                tx.object(this.poolLabel.versionId),
                tx.object(leg.positionCapId),
                tx.object(this.poolLabel.poolId),
                tx.pure.u64(leg.xTokens),
                tx.object(ALPHALEND_LENDING_PROTOCOL_ID),
                tx.object(SUI_SYSTEM_STATE),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        })[0]);
        const [slushCoin, ...rest] = coins;
        if (rest.length > 0) {
            tx.mergeCoins(slushCoin, rest);
        }
        this.context.blockchain.sendCoinToAddressBalance(tx, this.poolLabel.asset.type, options.address, slushCoin);
    }
    async claimRewards(_tx, _alphaReceipt) {
        return;
    }
}
//# sourceMappingURL=slushLending.js.map